import React, { useEffect, useState } from "react";

import { getPartnerAccountCandidates } from "services/partners/rest";
import { PartnerAccountCandidateDto } from "services/partners/restModels";
import { useDebouncedValue } from "shared/hooks/useDebouncedValue";
import { formatPhoneNumber } from "shared/lib/phoneNumber";
import { LabeledField } from "shared/ui/LabeledField";
import { SearchableSelect } from "shared/ui/SearchableSelect";

import { MutedLine } from "../styles";

const SEARCH_DEBOUNCE_MS = 300;

interface AccountSelectProps {
    value: PartnerAccountCandidateDto | null;
    disabled: boolean;
    onChange: (candidate: PartnerAccountCandidateDto | null) => void;
}

const candidateDetails = ({ name, organizationBranchName }: PartnerAccountCandidateDto) =>
    [name, organizationBranchName].filter(Boolean).join(" · ");

/*
* Номер, имя и заведение видны до сохранения: партнёр получает доступ в админку, и опечатка в
* номере не должна открыть её чужому человеку.
* */
export const AccountSelect = ({ value, disabled, onChange }: AccountSelectProps) => {
    const [query, setQuery] = useState("");
    const [options, setOptions] = useState<PartnerAccountCandidateDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
    const appliedQuery = query.trim() === "" ? "" : debouncedQuery.trim();

    useEffect(() => {
        if (appliedQuery === "") {
            setOptions([]);
            setLoading(false);
            setFailed(false);
            return undefined;
        }

        let ignore = false;

        setLoading(true);

        getPartnerAccountCandidates(appliedQuery)
            .catch(() => null)
            .then((response) => {
                if (ignore) return;

                setOptions(response || []);
                setFailed(response === null);
                setLoading(false);
            });

        return () => {
            ignore = true;
        };
    }, [appliedQuery]);

    const emptyText = () => {
        if (failed) return "Не удалось выполнить поиск";

        return appliedQuery === "" ? "Введите номер телефона" : "Аккаунты не найдены";
    };

    return (
        <LabeledField
            label="Аккаунт"
            hint="Сотрудники администрации и аккаунты других партнёров в список не попадают."
        >
            <SearchableSelect<PartnerAccountCandidateDto>
                value={value}
                onChange={onChange}
                options={options}
                getOptionKey={(option) => String(option.accountId)}
                getOptionLabel={(option) =>
                    [formatPhoneNumber(option.phoneNumber), candidateDetails(option)]
                        .filter(Boolean)
                        .join(" · ")
                }
                renderOption={(option) => (
                    <div>
                        <div>{formatPhoneNumber(option.phoneNumber)}</div>
                        <MutedLine>{candidateDetails(option) || "Без имени"}</MutedLine>
                    </div>
                )}
                query={query}
                onQueryChange={setQuery}
                loading={loading}
                placeholder="Выберите аккаунт"
                searchPlaceholder="Номер телефона"
                emptyText={emptyText()}
                disabled={disabled}
                title="Аккаунт"
            />
        </LabeledField>
    );
};
